import { useEffect, useState, useCallback, useRef } from 'react';
import type { BatchDraftExport } from '@pantry/shared';

export interface BatchDraftPersistenceConfig {
  isActive: boolean;
  draftItemCount: number;
  /** Must be a stable reference (module-scope const or useCallback). Unstable references cause infinite effect loops. */
  getPendingDraft: () => Promise<BatchDraftExport | null>;
  /** Must be a stable reference (module-scope const or useCallback). Unstable references cause infinite effect loops. */
  clearPendingDraft: () => Promise<void>;
  /** Must be a stable reference (Zustand action or useCallback). Unstable references cause infinite effect loops. */
  importDraft: (data: BatchDraftExport) => void;
}

export interface BatchDraftPersistenceResult {
  /** Whether we're still checking for a pending draft */
  isLoading: boolean;
  /** Whether there's a pending draft that can be resumed */
  hasPendingDraft: boolean;
  /** Number of items in the pending draft */
  pendingDraftCount: number;
  /** Resume the pending draft, importing items into the store */
  resumeDraft: () => void;
  /** Discard the pending draft and clear from storage */
  discardDraft: () => Promise<void>;
  /** Raw pending draft data (for advanced use) */
  pendingDraft: BatchDraftExport | null;
}

/**
 * Platform-agnostic core hook for batch draft persistence and recovery.
 * Checks for existing drafts on mount and provides functions to resume
 * or discard them. Both web (sync) and mobile (async) getPendingDraft
 * are supported since both return Promise from createPendingDraftHelpers.
 *
 * @example
 * ```ts
 * const result = useBatchDraftPersistenceCore({
 *   isActive,
 *   draftItemCount: draftItems.length,
 *   getPendingDraft,
 *   clearPendingDraft,
 *   importDraft,
 * });
 * ```
 */
export function useBatchDraftPersistenceCore(
  config: BatchDraftPersistenceConfig
): BatchDraftPersistenceResult {
  const { isActive, draftItemCount, getPendingDraft, clearPendingDraft, importDraft } = config;

  const [pendingDraft, setPendingDraft] = useState<BatchDraftExport | null>(null);
  const [isLoading, setIsLoading] = useState(() => !isActive && draftItemCount === 0);
  const hasCheckedRef = useRef(false);

  // Check for pending draft on mount (runs at most once)
  useEffect(() => {
    if (hasCheckedRef.current) return;

    async function checkForDraft() {
      // Only check if batch entry is not already active and no items are loaded
      if (!isActive && draftItemCount === 0) {
        hasCheckedRef.current = true;
        try {
          const draft = await getPendingDraft();
          setPendingDraft(draft);
        } catch {
          // Ignore errors — treat as no draft
        }
      }
      setIsLoading(false);
    }

    checkForDraft();
    // draftItemCount is listed for lint compliance but the hasCheckedRef guard
    // ensures the async check runs at most once, preventing re-fires on item changes.
  }, [isActive, draftItemCount, getPendingDraft]);

  /**
   * Resume the pending draft, importing items into the store.
   */
  const resumeDraft = useCallback(() => {
    if (pendingDraft) {
      importDraft(pendingDraft);
      setPendingDraft(null);
    }
  }, [pendingDraft, importDraft]);

  /**
   * Discard the pending draft and clear from storage.
   */
  const discardDraft = useCallback(async () => {
    await clearPendingDraft();
    setPendingDraft(null);
  }, [clearPendingDraft]);

  return {
    isLoading,
    hasPendingDraft: pendingDraft !== null && pendingDraft.items.length > 0,
    pendingDraftCount: pendingDraft?.items.length ?? 0,
    resumeDraft,
    discardDraft,
    pendingDraft,
  };
}
